import React from 'react';
import { t } from '../../i18n';
import { localizeError } from '../../i18n/errors';
import Modal from '../ui/Modal';
import { useFormatCurrency } from '../../utils/currencyContext';

// Host removal UI, shared by the lobby and Host tools. Small centered modals
// only (never a bottom sheet: mobile browser chrome covers those).

const secondaryBtn =
  'flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium disabled:opacity-40';
const dangerBtn =
  'flex-1 h-12 rounded-[12px] bg-accent-danger text-cream text-[14px] font-semibold disabled:opacity-40';

/** "Remove Alex?" with Cancel / Remove. */
export function RemovePlayerConfirm({ player, busy = false, error, onCancel, onConfirm }) {
  return (
    <Modal
      open={!!player}
      onClose={onCancel}
      dismissible={!busy}
      title={player ? t('game.removeTitle', { name: player.name }) : ''}
      compact
      zClass="z-[90]"
      testId="remove-confirm"
    >
      {error && (
        <p className="text-[13px] text-accent-danger mb-3 m-0" role="alert">{localizeError(error)}</p>
      )}
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={onCancel} disabled={busy} className={secondaryBtn}>
          {t('common.cancel')}
        </button>
        <button type="button" onClick={onConfirm} disabled={busy} className={dangerBtn}>
          {busy ? t('game.removing') : t('game.remove')}
        </button>
      </div>
    </Modal>
  );
}

/** Host tools → Remove player / Make host: tap a seated player. */
export function RemovePlayerPicker({
  open,
  players = [],
  onPick,
  onClose,
  title = t('game.removePlayer'),
  emptyText = t('game.removeEmpty'),
  hint,
  rowDetail,
  error,
  testId = 'remove-picker',
}) {
  const formatCurrency = useFormatCurrency();
  return (
    <Modal open={!!open} onClose={onClose} title={title} compact zClass="z-[80]" testId={testId}>
      {hint && (
        <p className="text-[13.5px] text-mute m-0 mb-2">{hint}</p>
      )}
      {error && (
        <p className="text-[13px] text-accent-danger mb-2 m-0" role="alert">{localizeError(error)}</p>
      )}
      {players.length === 0 ? (
        <p className="text-[13.5px] text-mute m-0 mb-2">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {players.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick?.(p)}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className="w-full h-12 px-4 rounded-[12px] border border-line bg-transparent flex items-center justify-between gap-3"
              data-testid={`pick-player-${p.name}`}
            >
              <span className="text-[14px] font-medium text-ink truncate">{p.name}</span>
              <span className="text-[13px] text-mute tnum whitespace-nowrap">
                {rowDetail ? rowDetail(p) : formatCurrency(p.stack || 0)}
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
